(() => {
  'use strict';
  if (window.heSpokeCartCelebration) return;
  window.heSpokeCartCelebration = true;

  const selector = '.hs15-progress[data-hs-cart-subtotal]';
  const initial = Number(document.currentScript?.dataset.hsInitialSubtotal);
  if (!Number.isFinite(initial)) return;
  let subtotal = initial;
  let queued = null;
  let debounce;
  let pending;
  let cleanup = () => {};
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const style = document.createElement('style');
  style.textContent = `
    .hs15-progress.hs-cart-celebrating{position:relative;overflow:hidden;animation:hs-cart-offer-highlight 1.1s ease both}
    .hs-cart-unlock-toast{position:absolute;inset:8px;z-index:2;display:flex;align-items:center;gap:10px;background:#eef4ed;color:#31583a;border:1px solid #d5e3d4;border-radius:6px;padding:16px;pointer-events:none;box-sizing:border-box;animation:hs-cart-toast-enter .35s cubic-bezier(.2,.9,.2,1.2) both}
    .hs-cart-unlock-check{display:flex;align-items:center;justify-content:center;flex:0 0 27px;height:27px;border-radius:50%;background:#31583a;color:#fff;font-size:17px;animation:hs-cart-check-pop .65s ease both}
    .hs-cart-unlock-toast strong{display:block;font-family:inherit;font-size:14px;line-height:1.45;font-weight:600}
    .hs-cart-unlock-toast small{display:block;font-family:inherit;font-size:12px;line-height:1.5;margin-top:4px}
    .hs-cart-confetti{position:absolute;left:50%;top:42%;width:5px;height:9px;z-index:3;pointer-events:none;background:var(--hs-confetti-color);animation:hs-cart-confetti 1s ease-out both}
    .hs15-progress.hs-cart-celebrating .hs15-track span{background:#31583a}
    @keyframes hs-cart-toast-enter{from{opacity:0;transform:translateY(-12px)}to{opacity:1;transform:translateY(0)}}
    @keyframes hs-cart-check-pop{0%,100%{transform:scale(1)}40%{transform:scale(1.25)}70%{transform:scale(.96)}}
    @keyframes hs-cart-offer-highlight{25%{background:#eef4ed}100%{background:#f7f6f2}}
    @keyframes hs-cart-confetti{from{opacity:1;transform:translate(0,0) rotate(0)}to{opacity:0;transform:translate(var(--hs-confetti-x),var(--hs-confetti-y)) rotate(var(--hs-confetti-rotation))}}
    @media(prefers-reduced-motion:reduce){.hs15-progress.hs-cart-celebrating,.hs-cart-unlock-toast,.hs-cart-unlock-check{animation:none}.hs-cart-confetti{display:none}}
  `;
  document.head.appendChild(style);

  function state(panel) {
    const value = Number(panel.dataset.hsCartSubtotal);
    const thresholds = (panel.dataset.hsCartThresholds || '').split(',').map(Number);
    const tier = Number(panel.dataset.hsCartTier);
    if (!Number.isFinite(value) || !Number.isInteger(tier) ||
        !thresholds.length || thresholds.some(value => !Number.isFinite(value) || value <= 0)) return null;
    return { panel, value, tier, thresholds };
  }

  function visible(panel) {
    if (!panel?.isConnected) return false;
    const box = panel.getBoundingClientRect();
    if (!box.width || !box.height || box.right <= 0 || box.left >= window.innerWidth ||
        box.bottom <= 0 || box.top >= window.innerHeight) return false;
    for (let node = panel; node instanceof Element; node = node.parentElement) {
      const appearance = window.getComputedStyle(node);
      if (appearance.display === 'none' || appearance.visibility === 'hidden' || appearance.opacity === '0') return false;
    }
    return true;
  }

  function celebrate(panel) {
    cleanup();
    const title = panel.dataset.hsUnlockMessage;
    const note = panel.dataset.hsUnlockNote;
    if (!title || !note) return;
    const toast = document.createElement('div');
    toast.className = 'hs-cart-unlock-toast';
    toast.setAttribute('aria-hidden', 'true');
    const check = document.createElement('span');
    check.className = 'hs-cart-unlock-check';
    check.textContent = '✓';
    const content = document.createElement('div');
    const heading = document.createElement('strong');
    heading.textContent = title;
    const detail = document.createElement('small');
    detail.textContent = note;
    content.append(heading, detail);
    toast.append(check, content);
    panel.classList.add('hs-cart-celebrating');
    panel.appendChild(toast);
    const particles = [];
    if (!motion.matches) {
      const colors = ['#31583a', '#c97860', '#171717'];
      for (let i = 0; i < 18; i++) {
        const bit = document.createElement('span');
        bit.className = 'hs-cart-confetti';
        bit.setAttribute('aria-hidden', 'true');
        bit.style.setProperty('--hs-confetti-color', colors[i % colors.length]);
        bit.style.setProperty('--hs-confetti-x', `${(i % 2 ? -1 : 1) * (20 + i * 9)}px`);
        bit.style.setProperty('--hs-confetti-y', `${35 + (i % 5) * 25}px`);
        bit.style.setProperty('--hs-confetti-rotation', `${i * 61}deg`);
        panel.appendChild(bit);
        particles.push(bit);
      }
    }
    const clearParticles = () => particles.forEach(bit => bit.remove());
    const particleTimer = window.setTimeout(clearParticles, 1100);
    const toastTimer = window.setTimeout(() => cleanup(), 2300);
    cleanup = () => {
      window.clearTimeout(particleTimer);
      window.clearTimeout(toastTimer);
      clearParticles();
      toast.remove();
      panel.classList.remove('hs-cart-celebrating');
      cleanup = () => {};
    };
  }

  function revealPending() {
    if (!pending) return;
    if (Date.now() > pending.expires || !pending.panel.isConnected) {
      pending = null;
      return;
    }
    if (visible(pending.panel)) {
      const panel = pending.panel;
      pending = null;
      celebrate(panel);
    }
  }

  function reconcile() {
    const next = queued;
    queued = null;
    if (!next) {
      revealPending();
      return;
    }
    if (next.empty) {
      subtotal = 0;
      pending = null;
      cleanup();
      return;
    }
    if (!next.panel.isConnected) return;
    const before = next.thresholds.filter(threshold => subtotal >= threshold * 100).length;
    const increased = next.tier > before && next.value > subtotal;
    const changed = next.value !== subtotal;
    subtotal = next.value;
    if (changed) {
      pending = null;
      cleanup();
    }
    if (increased) {
      pending = { panel: next.panel, expires: Date.now() + 2500 };
      revealPending();
      window.setTimeout(revealPending, 350);
      window.setTimeout(revealPending, 900);
    } else {
      if (pending && !pending.panel.isConnected && next.tier > 0) pending.panel = next.panel;
      revealPending();
    }
  }

  function inspect(node) {
    if (!(node instanceof Element)) return;
    const panels = node.matches(selector) ? [node] : [...node.querySelectorAll(selector)];
    for (const panel of panels) {
      const value = state(panel);
      if (value) queued = value;
    }
    if (!panels.length && (node.matches('.previewCart-emptyBody') || node.querySelector('.previewCart-emptyBody'))) {
      queued = { empty: true };
    }
  }

  function start() {
    const observer = new MutationObserver(records => {
      let relevant = false;
      for (const record of records) {
        if (record.type === 'childList') {
          for (const node of record.addedNodes) {
            if (!(node instanceof Element) || node.matches('.hs-cart-unlock-toast,.hs-cart-confetti') ||
                node.closest('.hs-cart-unlock-toast')) continue;
            inspect(node);
            if (queued) relevant = true;
          }
        } else if (record.attributeName === 'data-hs-cart-subtotal') {
          inspect(record.target);
          relevant = true;
        } else if (pending && record.target.contains(pending.panel) && record.target !== pending.panel) {
          relevant = true;
        }
      }
      if (relevant) {
        window.clearTimeout(debounce);
        debounce = window.setTimeout(reconcile, 80);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true,
      attributeFilter: ['class', 'style', 'data-hs-cart-subtotal'] });
    motion.addEventListener('change', () => {
      if (motion.matches) document.querySelectorAll('.hs-cart-confetti').forEach(bit => bit.remove());
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
